import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import { buildCanonicalUrl } from "lib/seo";

import type { GetServerSideProps } from "next";
import type { RegistrationData, TalkData } from "membership-ui/login/fetcher";
import type { Modify } from "lib/types/common";
import type { FlowType, Newsletters } from "membership-ui/login/types";
import { NewsletterVariant } from "lib/types/sitewideOption";

import {
  fetchRegistrationData,
  fetchTalksIdData,
} from "membership-ui/login/fetcher";
import { fetchLoginFlowLogoOverwrite } from "lib/fetchers/siteWide/loginFlowLogoOverwrite";

import { LoginFlowLogo } from "components/icons/LoginFlowLogo";
import ErrorBoundary from "components/errorBoundary/errorBoundary";
import { seoMeta } from "membership-ui/login/utils/handleStaticSEOMeta";
import { fetchIntelligencePageData } from "content-ui/intelligence/fetcher";
import type { Report } from "content-ui/intelligence/types";
import { fetchBreakfastShortById } from "content-ui/breakfast/fetcher";
import { BreakfastMinimumLoginPageData } from "content-ui/breakfast/types";
import logger from "lib/logger";
import LoginRegistrationForm from "components/zephr/LoginRegistrationForm";
import { fetchNewslettersList } from "lib/fetchers/siteWide";
import LoginRegisterLayout from "pages/members/login/LoginRegisterLayout";

type GetServerSideBaseRedirectProps = Modify<
  GetServerSideProps,
  {
    basePath?: boolean;
  }
>;

export const getServerSideProps: GetServerSideBaseRedirectProps = async ({
  query,
  preview = false,
}) => {
  let selectedReport: Report | null = null;
  let flow: FlowType = "general";
  const talkId = (query?.talkId as string) || (query?.talkid as string);
  const breakfastId =
    (query?.breakfastId as string) || (query?.breakfastid as string);
  const talksData = talkId
    ? fetchTalksIdData({ talkId, preview })
    : Promise.resolve({});
  const intelligenceData = query?.report
    ? fetchIntelligencePageData({ preview })
    : Promise.resolve(null);

  const redirect = (path: string = "/") => {
    return {
      redirect: {
        destination: path,
        permanent: false,
        basePath: false,
      },
    };
  };

  try {
    const [
      talkData,
      reportsData,
      registrationData,
      newslettersList,
      breakfastData,
    ] = await Promise.all([
      talksData,
      intelligenceData,
      fetchRegistrationData({ preview }),
      fetchNewslettersList(),
      breakfastId
        ? fetchBreakfastShortById({ preview, id: breakfastId })
        : Promise.resolve(null),
    ]);

    const {
      url: loginFlowLogoOverwriteUrl,
      alt: loginFlowLogoAlt,
      width: loginFlowLogoWidth,
      height: loginFlowLogoHeight,
    } = await fetchLoginFlowLogoOverwrite(preview);

    // If the user has tampered with the query params, send them back to the talks page
    if (talkId) {
      flow = "talk";
      const hasConsented = query.marketingConsent === "true";
      const consentNotRequired = query.marketingConsent === "null";
      const isValidConsentValue = hasConsented || consentNotRequired;
      if (!isValidConsentValue) {
        return redirect("/talks");
      }
    }

    // If the user has tampered with query params, send them back to the breakfast page
    if (breakfastId) {
      flow = "breakfast";
      const hasConsented = query.marketingConsent === "true";

      if (!hasConsented) {
        return redirect("/breakfast");
      }
    }

    const validNewsletterValues: Newsletters[] = [
      "sifted",
      "climatetech",
      "startuplife",
      "upround",
      "fintech",
      "deeptech",
    ];

    // Check the newsletter param, and only pass to the front-end if it is valid
    if (query?.newsletter) {
      if (validNewsletterValues.indexOf(query?.newsletter) === -1) {
        delete query.newsletter;
      } else {
        flow = "newsletter";
      }
    }

    if (query?.report) {
      selectedReport =
        reportsData?.reports.filter(
          (report: Report) =>
            report.title.toLowerCase() === query.report.toLowerCase()
        )?.[0] || null;

      if (!selectedReport) {
        delete query.report;
      } else {
        flow = "report";
      }
    }

    return {
      props: {
        talkData,
        registrationData,
        breakfastData,
        newslettersList,
        seoMeta: seoMeta(query, newslettersList),
        flow,
        report: selectedReport,
        loginFlowLogoOverwriteUrl,
        loginFlowLogoAlt,
        loginFlowLogoWidth,
        loginFlowLogoHeight,
        __initialState: {
          sitewideOption: { newslettersList },
        },
      },
    };
  } catch (error) {
    logger.error("Error in reg flow - pages/members/login/index.tsx", {
      error,
    });
    return redirect("/");
  }
};

type PageProps = {
  talkData: TalkData;
  registrationData: RegistrationData;
  breakfastData: BreakfastMinimumLoginPageData | null;
  newslettersList: NewsletterVariant[];
  seoMeta: {
    title: string;
    description: string;
    seoImage: string;
  };
  flow: FlowType;
  report?: Report;
};

type PagePropsWithLogo = PageProps & {
  loginFlowLogoOverwriteUrl?: string | null;
  loginFlowLogoAlt?: string | null;
  loginFlowLogoWidth?: number | null;
  loginFlowLogoHeight?: number | null;
};

const LoginPage = (props: PagePropsWithLogo) => {
  const {
    seoMeta,
    newslettersList,
    loginFlowLogoOverwriteUrl,
    loginFlowLogoWidth,
    loginFlowLogoHeight,
  } = props;
  return (
    <>
      <Head>
        {generateNextSeo({
          canonical: buildCanonicalUrl("/members/login"),
          openGraph: {
            title: seoMeta?.title,
            description: seoMeta?.description,
            images: seoMeta?.seoImage
              ? [
                  {
                    url: seoMeta.seoImage,
                  },
                ]
              : [],
          },
          twitter: {
            handle: "@siftedeu",
            site: "@siftedeu",
            cardType: "summary_large_image",
          },
        })}
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <ErrorBoundary>
        <LoginRegisterLayout
          logo={
            <LoginFlowLogo
              externalSrc={loginFlowLogoOverwriteUrl}
              alt={props.loginFlowLogoAlt ?? undefined}
              width={loginFlowLogoWidth ?? undefined}
              height={loginFlowLogoHeight ?? undefined}
            />
          }
          form={<LoginRegistrationForm newslettersList={newslettersList} />}
        />
      </ErrorBoundary>
    </>
  );
};

export default LoginPage;
